import { DomainCaseStatus } from './enums';

/**
 * A removal as the client sees it: the steps, never the correspondence
 * (DEC-001, DEC-002). Only steps that really happened are listed, because
 * "Delisting requested" is the one a client can check with Google herself.
 */
export const ClientStep = {
  FOUND: 'found',
  PROCESSING: 'processing',
  DELISTING_REQUESTED: 'delisting_requested',
  REMOVED: 'removed',
  WATCHING: 'watching',
  BACK: 'back',
  FILED_AGAIN: 'filed_again',
  /** Case closed because she (or we) put the host on her whitelist — history
   *  stays, work stops; not the same as Removed. */
  WHITELISTED: 'whitelisted',
} as const;

export type ClientStep = (typeof ClientStep)[keyof typeof ClientStep];

export interface ClientStepAt {
  step: ClientStep;
  /** When it happened, or null when the step is a state rather than a moment. */
  at: string | null;
}

export function clientJourney(input: {
  openedAt: Date;
  status: string;
  resolvedAt: Date | null;
  /** When the page this round is about came back up; null on a first round. */
  returnedAt: Date | null;
  /** The first notice of the round on any channel. */
  firstSentAt: Date | null;
  delistingSentAt: Date | null;
  /** Why a closed case closed; `whitelisted` is the client/global whitelist path. */
  closeReason?: string | null;
}): ClientStepAt[] {
  const iso = (at: Date | null) => at?.toISOString() ?? null;
  const steps: ClientStepAt[] = [];

  if (input.returnedAt) {
    // A round opened because the page came back: it starts at Back, and the
    // client sees us straight onto it again (DEC-012).
    steps.push({ step: ClientStep.BACK, at: iso(input.returnedAt) });
    steps.push(
      input.firstSentAt
        ? { step: ClientStep.FILED_AGAIN, at: iso(input.firstSentAt) }
        : { step: ClientStep.PROCESSING, at: iso(input.openedAt) },
    );
  } else {
    steps.push({ step: ClientStep.FOUND, at: iso(input.openedAt) });
    steps.push({ step: ClientStep.PROCESSING, at: iso(input.openedAt) });
  }

  if (input.delistingSentAt) {
    steps.push({ step: ClientStep.DELISTING_REQUESTED, at: iso(input.delistingSentAt) });
  }

  if (input.status === DomainCaseStatus.DONE) {
    steps.push({ step: ClientStep.REMOVED, at: iso(input.resolvedAt) });
    steps.push({ step: ClientStep.WATCHING, at: null });
  } else if (
    input.status === DomainCaseStatus.CANCELLED
    && (input.closeReason === 'whitelisted' || input.closeReason === 'invalid')
  ) {
    // Drop the open "Processing" tip: work stopped; history keeps Found then
    // Whitelisted so Removals does not read as Still open / Processing.
    const openTip = steps[steps.length - 1];
    if (openTip?.step === ClientStep.PROCESSING || openTip?.step === ClientStep.FILED_AGAIN) {
      steps.pop();
    }
    steps.push({ step: ClientStep.WHITELISTED, at: iso(input.resolvedAt) });
  }
  return steps;
}

/** The step a list shows for a case: the last one it reached. */
export function currentStep(journey: readonly ClientStepAt[]): ClientStep {
  return journey[journey.length - 1]?.step ?? ClientStep.FOUND;
}
